import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DeleteOutlined, StarFilled } from "@ant-design/icons";
import { Button, Col, Input, Popconfirm, Row, Space, Switch, Table, Typography } from "antd";
import { useEffect, useState } from "react";
import { ETemplateMetaDataVersionUpdateState, EUserRole, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { debounce } from "@devowl-wp/react-frontend";
import { useTemplateMetaDataColumns } from "./columns.js";
import { useTranslation } from "../../../../../others/i18n.js";
import { useStores } from "../../../../../store/root.js";
import { spacing } from "../../../../../styles/spacing.js";
import { updateFilterFavoriteState } from "../../../../../util/store/settings.js";
import { PermissionCheck } from "../../user/permission.js";
function TemplateMetaDataTable({ templateMetaData, loading, tableTitle, tableInfoNotice, actions = {}, rowSelection = undefined, pagination = false, extraColumns = [], extraColumnsPosition = "post", hiddenColumns = [], size, expandable }) {
    // Generals
    const { t } = useTranslation();
    const { rcbAuthenticationStore, rcbUserSettingsStore } = useStores();
    const { settings: userSettings } = rcbUserSettingsStore;
    // Filters
    const [data, setData] = useState(templateMetaData);
    const [search, setSearch] = useState("");
    const [showFavouritesOnly, setShowFavouritesOnly] = useState(userSettings?.filter?.showFavouritesOnly !== undefined ? userSettings.filter.showFavouritesOnly : !rcbAuthenticationStore.hasRole(EUserRole.Creator));
    const toggleFavouriteSwitch = (checked)=>{
        updateFilterFavoriteState(checked, rcbUserSettingsStore);
        setShowFavouritesOnly(checked);
        actions?.onFilterFavouriteToggle?.(checked);
    };
    const [isSearchHidden, setIsSearchHidden] = useState(false);
    useEffect(()=>{
        setIsSearchHidden(pagination ? (pagination.pageSize || pagination.defaultPageSize) >= templateMetaData?.length : false);
    }, [
        pagination,
        templateMetaData
    ]);
    useEffect(()=>{
        if (templateMetaData) {
            setData(templateMetaData.filter((template)=>{
                let matching = true;
                if (search !== "") {
                    matching = !!template.headline?.toLowerCase().includes(search.toLowerCase());
                    matching = matching || !!template.subHeadline?.toLowerCase().includes(search.toLowerCase());
                    matching = matching || !!template.extends?.headline?.toLowerCase().includes(search.toLowerCase());
                    matching = matching || !!template.extends?.subHeadline?.toLowerCase().includes(search.toLowerCase());
                }
                if (showFavouritesOnly) {
                    matching = matching && template.isFavourite;
                }
                return matching;
            }));
        }
    }, [
        templateMetaData,
        search,
        showFavouritesOnly
    ]);
    let columns = useTemplateMetaDataColumns(actions);
    if (!rcbAuthenticationStore.hasRole(EUserRole.Creator)) {
        hiddenColumns.push(TemplateMetaDataField.Tier, TemplateMetaDataField.Status);
    }
    if (extraColumns?.length > 0) {
        if (!isNaN(Number(extraColumnsPosition))) {
            const colIndex = Number(extraColumnsPosition);
            const insertIndex = colIndex < columns.length ? colIndex : columns.length - 1;
            columns.splice(insertIndex, 0, ...extraColumns);
        } else {
            switch(extraColumnsPosition){
                case "pre":
                    columns.splice(0, 0, ...extraColumns);
                    break;
                case "post":
                    columns.push(...extraColumns);
                    break;
            }
        }
    }
    if (hiddenColumns.length > 0) {
        columns = columns.filter((column)=>!hiddenColumns.includes(column["dataIndex"]));
    }
    if (actions.onEdit || actions.onDelete || actions.onDiff) {
        columns.push({
            title: t("Action"),
            key: "action",
            render: (_, record)=>{
                const deleteTitle = actions?.onDeleteTitle ? actions.onDeleteTitle : t("Delete");
                return /*#__PURE__*/ _jsxs(Space, {
                    size: "small",
                    children: [
                        actions.onEdit && !record.isDeleted && /*#__PURE__*/ _jsx(Button, {
                            onClick: ()=>actions.onEdit(record),
                            type: "primary",
                            children: actions?.onEditTitle ? actions.onEditTitle : t("Edit")
                        }, `${record.id}-edit`),
                        actions.onDelete && /*#__PURE__*/ _jsx(PermissionCheck, {
                            minRole: EUserRole.Admin,
                            children: /*#__PURE__*/ _jsx(Popconfirm, {
                                title: `${deleteTitle} ${record.headline} ${record.subHeadline ? ` (${record.subHeadline})` : ""}`,
                                okText: deleteTitle,
                                onConfirm: ()=>new Promise((resolve)=>{
                                        actions.onDelete(record);
                                        resolve(null);
                                    }),
                                placement: "topRight",
                                icon: /*#__PURE__*/ _jsx(DeleteOutlined, {
                                    style: {
                                        color: "red"
                                    }
                                }),
                                children: /*#__PURE__*/ _jsx(Button, {
                                    children: deleteTitle
                                })
                            })
                        }),
                        actions.onDiff && record.releaseStatus !== ETemplateMetaDataVersionUpdateState.NeverReleased && /*#__PURE__*/ _jsx(Button, {
                            onClick: ()=>actions.onDiff(record),
                            type: "primary",
                            children: actions?.onDiffTitle ? actions.onDiffTitle : t("Compare")
                        }, `${record.id}-diff`)
                    ]
                });
            },
            align: "right",
            width: "5rem"
        });
    }
    const debounceSearch = debounce((value)=>setSearch(value), 250);
    // UI
    const { Search } = Input;
    const { Title } = Typography;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                style: {
                    paddingBottom: "1rem"
                },
                children: [
                    /*#__PURE__*/ _jsx(Col, {
                        children: /*#__PURE__*/ _jsx(Title, {
                            ...tableTitle.props || {
                                level: 3
                            },
                            children: tableTitle.title
                        })
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        hidden: !!actions.favouriteToggleHidden,
                        children: /*#__PURE__*/ _jsxs(Space, {
                            style: {
                                paddingTop: spacing.layout.content.paddingSmall
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Switch, {
                                    defaultChecked: showFavouritesOnly,
                                    checkedChildren: /*#__PURE__*/ _jsx(StarFilled, {
                                        style: {
                                            color: "orange"
                                        }
                                    }),
                                    onClick: (checked)=>toggleFavouriteSwitch(checked),
                                    style: {
                                        float: "left",
                                        marginLeft: "1rem"
                                    }
                                }),
                                " ",
                                t("Favourites Only")
                            ]
                        })
                    }),
                    actions.onCreate && /*#__PURE__*/ _jsx(PermissionCheck, {
                        minRole: EUserRole.Creator,
                        children: /*#__PURE__*/ _jsx(Col, {
                            style: {
                                float: "left"
                            },
                            children: /*#__PURE__*/ _jsx(Button, {
                                onClick: ()=>actions.onCreate(),
                                style: {
                                    float: "left",
                                    marginLeft: "1rem"
                                },
                                type: "primary",
                                children: actions?.onCreateTitle ? actions.onCreateTitle : t("Add")
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        flex: "auto"
                    }),
                    /*#__PURE__*/ _jsx(Col, {
                        style: {
                            float: "right"
                        },
                        hidden: isSearchHidden,
                        children: /*#__PURE__*/ _jsx(Search, {
                            style: {
                                minWidth: "20rem",
                                textAlign: "left"
                            },
                            placeholder: t("Search template ..."),
                            onSearch: (search)=>{
                                setSearch(search);
                            },
                            onChange: (event)=>debounceSearch(event.target.value),
                            enterButton: true,
                            allowClear: true
                        })
                    })
                ]
            }),
            tableInfoNotice ? /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsx(Col, {
                    flex: "auto",
                    style: {
                        marginBottom: spacing.layout.content.marginMedium
                    },
                    children: tableInfoNotice
                })
            }) : null,
            /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsx(Col, {
                    children: /*#__PURE__*/ _jsx(Table, {
                        tableLayout: "auto",
                        loading: loading,
                        columns: columns,
                        dataSource: data,
                        pagination: pagination,
                        rowKey: "id",
                        sticky: {
                            offsetHeader: spacing.layout.content.padding * -1
                        },
                        size: size,
                        rowSelection: rowSelection,
                        expandable: expandable
                    })
                })
            })
        ]
    });
}
export { TemplateMetaDataTable };
